import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  Plus, 
  Edit, 
  Trash2,
  Loader2, 
  Building2,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OfficeLocationsSettings = () => {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [deletingOffice, setDeletingOffice] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    is_active: true
  });

  useEffect(() => {
    fetchOffices();
  }, []);

  const fetchOffices = async () => {
    try {
      const response = await axios.get(`${API}/admin/office-locations`);
      setOffices(response.data.offices || []);
    } catch (error) {
      toast.error('Failed to load office locations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (office = null) => {
    if (office) {
      setEditingOffice(office);
      setFormData({
        name: office.name || '',
        code: office.code || '',
        address: office.address || '',
        city: office.city || '',
        state: office.state || '',
        zip: office.zip || '',
        phone: office.phone || '',
        is_active: office.is_active !== false
      });
    } else {
      setEditingOffice(null);
      setFormData({
        name: '',
        code: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        is_active: true
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingOffice) {
        await axios.put(`${API}/admin/office-locations/${editingOffice.id}`, formData);
        toast.success('Office location updated successfully');
      } else {
        await axios.post(`${API}/admin/office-locations`, formData);
        toast.success('Office location created successfully');
      }
      setShowDialog(false);
      fetchOffices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save office location');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingOffice) return;
    setSaving(true);

    try {
      await axios.delete(`${API}/admin/office-locations/${deletingOffice.id}`);
      toast.success('Office location deleted successfully');
      setShowDeleteDialog(false);
      setDeletingOffice(null);
      fetchOffices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete office location');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]" data-testid="office-locations-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="office-locations-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Office Locations</h2>
          <p className="text-gray-500">Configure office locations for user assignment</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-office-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Office
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {offices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No office locations configured</p>
              <p className="text-sm">Click &quot;Add Office&quot; to create one</p>
            </div>
          ) : (
            <div className="divide-y">
              {offices.map((office) => (
                <div 
                  key={office.id} 
                  className="py-4 flex items-center justify-between"
                  data-testid={`office-row-${office.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{office.name}</span>
                      {office.code && (
                        <Badge variant="outline">{office.code}</Badge>
                      )}
                      <Badge variant={office.is_active ? "default" : "secondary"}>
                        {office.is_active ? (
                          <><CheckCircle className="w-3 h-3 mr-1" />Active</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" />Inactive</>
                        )}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {[office.city, office.state].filter(Boolean).join(', ')}
                      {office.phone && ` • ${office.phone}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleOpenDialog(office)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        setDeletingOffice(office);
                        setShowDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingOffice ? 'Edit Office Location' : 'Add Office Location'}</DialogTitle>
            <DialogDescription>
              Configure office details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Office Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Nashville"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="NSH"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="123 Main St"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="Nashville"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  placeholder="TN"
                  maxLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">ZIP</Label>
                <Input
                  id="zip"
                  value={formData.zip}
                  onChange={(e) => setFormData({...formData, zip: e.target.value})}
                  placeholder="37201"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(615) 555-0100"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingOffice ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Office Location
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deletingOffice?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfficeLocationsSettings;
